import { Link, useNavigate, useRouterState } from '@tanstack/react-router'
import { useAuth } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { LogOut, ListFilter, Shield, Menu, X } from 'lucide-react'
import { useState } from 'react'

type NavbarProps = {
  onToggleQueue?: () => void
  isQueueOpen?: boolean
  queueCount?: number
}

export function Navbar({ onToggleQueue, isQueueOpen = false, queueCount = 0 }: NavbarProps) {
  const navigate = useNavigate()
  const { user, logout, isAuthenticated } = useAuth()
  const router = useRouterState()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  // Get the current path to determine active page
  const currentPath = router.location.pathname

  const handleLogout = async () => {
    try {
      await logout.mutateAsync()
      navigate({ to: '/auth/login', search: { redirect: undefined } })
    } catch (error) {
      console.error('Logout failed:', error)
      navigate({ to: '/auth/login', search: { redirect: undefined } })
    }
  }

  // Helper to determine if a path is active
  const isActive = (path: string) => currentPath.startsWith(path)

  return (
    <nav className={`sticky top-0 z-30 border-b border-border bg-card/90 backdrop-blur supports-[backdrop-filter]:bg-card/70 transition-all duration-200 ease-in-out ${isQueueOpen ? 'pr-80' : ''}`}>
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            search={{}}
            className="text-xl font-semibold tracking-wide uppercase text-primary hover:text-accent transition-colors"
          >
            art suite
          </Link>

          {isAuthenticated ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="hidden md:flex items-center space-x-1">
                <Button variant={isActive('/generation') ? 'default' : 'secondary'} size="sm" asChild>
                  <Link to="/generation" search={{
                    loraId: undefined,
                    modelSlug: undefined,
                    prompt: undefined,
                    width: undefined,
                    height: undefined,
                    steps: undefined,
                    cfgScale: undefined,
                    clipSkip: undefined,
                    seedMode: undefined,
                    seed: undefined,
                    loras: undefined,
                    workflowVariant: undefined,
                  }} className="text-xs font-medium">
                    Generate
                  </Link>
                </Button>
                <Button variant={isActive('/upscale') ? 'default' : 'secondary'} size="sm" asChild>
                  <Link to="/upscale" search={{}} className="text-xs font-medium">
                    Upscale
                  </Link>
                </Button>
                <Button variant={isActive('/edit') ? 'default' : 'secondary'} size="sm" asChild>
                  <Link to="/edit" search={{}} className="text-xs font-medium">
                    Edit
                  </Link>
                </Button>
                <Button variant={isActive('/video') ? 'default' : 'secondary'} size="sm" asChild>
                  <Link to="/video" search={{}} className="text-xs font-medium">
                    Video
                  </Link>
                </Button>
                <Button variant={isActive('/gallery') ? 'default' : 'secondary'} size="sm" asChild>
                  <Link to="/gallery" search={{}} className="text-xs font-medium">
                    Gallery
                  </Link>
                </Button>
                <Button variant={isActive('/loras') ? 'default' : 'secondary'} size="sm" asChild>
                  <Link to="/loras" search={{}} className="text-xs font-medium">
                    LoRAs
                  </Link>
                </Button>
              </div>
            </div>
          ) : null}

          {/* Mobile menu button */}
          {isAuthenticated && (
            <Button
              variant="ghost"
              size="sm"
              className="md:hidden"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          )}

          {isAuthenticated ? (
            <div className="hidden md:flex items-center space-x-2">
              <Button variant="ghost" size="default" onClick={onToggleQueue} className="relative">
                <ListFilter className="h-4 w-4 mr-2" />
                Queue
                {queueCount > 0 && (
                  <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
                    {queueCount > 99 ? '99+' : queueCount}
                  </span>
                )}
              </Button>

              {/* Admin link - only show to mandrew0987@gmail.com */}
              {user?.email === 'mandrew0987@gmail.com' && (
                <Button variant={isActive('/admin') ? 'default' : 'outline'} size="sm" asChild>
                  <Link to="/admin/approve-users" className="text-xs font-medium">
                    <Shield className="h-3 w-3 mr-1" />
                    Admin
                  </Link>
                </Button>
              )}

              <Button variant="destructive" size="sm" onClick={handleLogout} disabled={logout.isPending}>
                <LogOut className="h-3 w-3 mr-1" />
                {logout.isPending ? 'Signing out…' : 'Sign out'}
              </Button>
            </div>
          ) : (
            <div className="flex items-center space-x-2">
              <Button variant="ghost" size="sm" asChild>
                <Link to="/auth/login" search={{ redirect: undefined }}>
                  Log In
                </Link>
              </Button>
              <Button variant="default" size="sm" asChild>
                <Link to="/auth/register" search={{}}>
                  Sign Up
                </Link>
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Mobile menu */}
      {isAuthenticated && isMobileMenuOpen && (
        <div className="md:hidden border-t border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80">
          <div className="px-4 pt-2 pb-3 space-y-1">
            <Button variant={isActive('/generation') ? 'default' : 'ghost'} size="sm" asChild className="w-full justify-start">
              <Link to="/generation" search={{
                loraId: undefined,
                modelSlug: undefined,
                prompt: undefined,
                width: undefined,
                height: undefined,
                steps: undefined,
                cfgScale: undefined,
                clipSkip: undefined,
                seedMode: undefined,
                seed: undefined,
                loras: undefined,
                workflowVariant: undefined,
              }} onClick={() => setIsMobileMenuOpen(false)}>
                Generate
              </Link>
            </Button>
            <Button variant={isActive('/upscale') ? 'default' : 'ghost'} size="sm" asChild className="w-full justify-start">
              <Link to="/upscale" search={{}} onClick={() => setIsMobileMenuOpen(false)}>
                Upscale
              </Link>
            </Button>
            <Button variant={isActive('/edit') ? 'default' : 'ghost'} size="sm" asChild className="w-full justify-start">
              <Link to="/edit" search={{}} onClick={() => setIsMobileMenuOpen(false)}>
                Edit
              </Link>
            </Button>
            <Button variant={isActive('/video') ? 'default' : 'ghost'} size="sm" asChild className="w-full justify-start">
              <Link to="/video" search={{}} onClick={() => setIsMobileMenuOpen(false)}>
                Video
              </Link>
            </Button>
            <Button variant={isActive('/gallery') ? 'default' : 'ghost'} size="sm" asChild className="w-full justify-start">
              <Link to="/gallery" search={{}} onClick={() => setIsMobileMenuOpen(false)}>
                Gallery
              </Link>
            </Button>
            <Button variant={isActive('/loras') ? 'default' : 'ghost'} size="sm" asChild className="w-full justify-start">
              <Link to="/loras" search={{}} onClick={() => setIsMobileMenuOpen(false)}>
                LoRAs
              </Link>
            </Button>
          </div>

          <div className="border-t border-border px-4 pt-2 pb-3 space-y-1">
            {/* Admin link - only show to mandrew0987@gmail.com */}
            {user?.email === 'mandrew0987@gmail.com' && (
              <Button variant={isActive('/admin') ? 'default' : 'ghost'} size="sm" asChild className="w-full justify-start">
                <Link to="/admin/approve-users" onClick={() => setIsMobileMenuOpen(false)}>
                  <Shield className="h-4 w-4 mr-2" />
                  Admin
                </Link>
              </Button>
            )}

  
            <Button variant="destructive" size="sm" onClick={handleLogout} disabled={logout.isPending} className="w-full justify-start">
              <LogOut className="h-4 w-4 mr-2" />
              {logout.isPending ? 'Signing out…' : 'Sign out'}
            </Button>
          </div>
        </div>
      )}
    </nav>
  )
}
